import React, { PureComponent } from 'react';
import { Flex } from '../../common/Flex';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { Button, Col, notification, Radio, Row, Select, Space, Tabs } from 'antd';
import { SettingFilled } from '@ant-design/icons';
import {
    FLIGHT_LIST_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
    FlightDto,
    FlightListByTvRequestDto,
    MeasureId,
    REGULATION_LIST_REQUEST_DTO_CLAZZ_VALUE,
    RegulationFieldType,
    RegulationOrMcdmOnlyDto,
    StandardFlightFieldType,
    TimeWindowType,
    TrafficType,
} from 'app/model/CORE';
import TimeUtils from '../../../common/util/TimeUtils';
import { ModalPanelProps } from '../DashboardModal';
import { RegulationInteractionGraph } from './interaction/RegulationInteractionGraph';
import { DisplayData, MeasureLoadData, RegulationInteractionUtils } from './interaction/RegulationInteractionUtils';
import { RegulationInteractionSummary } from './interaction/RegulationInteractionSummary';
import TrafficCountUtils from '../../../common/util/TrafficCountUtils';
import { MeasureLoadBar } from '../../dashboard/network/flows/svg/MeasureLoadBar';
import FlightUtils from '../../../common/util/FlightUtils';
import './InteractionStudyPanel.less';
import { ModalHandler } from '../ModalHandler';
import AppContext from 'app/common/context/AppContext';

// First color in the list will be the color of the studied regulation
const COLOR_PALETTE = [
    '#BFBFBF',
    '#FFFF00',
    '#1CE6FF',
    '#344fff',
    '#FF4A46',
    '#008941',
    '#006FA6',
    '#A30059',
    '#FFDBE5',
    '#7A4900',
    '#63FFAC',
    '#B79762',
    '#004D43',
    '#8FB0FF',
    '#997D87',
    '#5A0007',
    '#809693',
    '#FEFFE6',
    '#1B4400',
    '#4FC601',
    '#3B5DFF',
    '#FF2F80',
    '#BA0900',
    '#6B7900',
    '#00C2A0',
    '#FFAA92',
    '#FF90C9',
    '#B903AA',
    '#D16100',
    '#DDEFFF',
    '#7B4F4B',
    '#A1C299',
    '#0AA6D8',
    '#013349',
    '#00846F',
    '#FFB500',
    '#C2FFED',
    '#A079BF',
    '#CC0744',
    '#C0B9B2',
    '#C2FF99',
    '#00489C',
    '#6F0062',
    '#0CBD66',
    '#EEC3FF',
    '#456D75',
    '#B77B68',
    '#7A87A1',
    '#788D66',
    '#885578',
    '#FAD09F',
    '#FF8A9A',
    '#D157A0',
    '#BEC459',
    '#456648',
    '#0086ED',
    '#886F4C',
    '#B4A8BD',
    '#00A6AA',
    '#A3C8C9',
    '#FF913F',
    '#938A81',
    '#575329',
    '#00FECF',
    '#B05B6F',
    '#8CD0FF',
    '#3B9700',
    '#04F757',
    '#C8A1A1',
    '#1E6E00',
    '#7900D7',
    '#A77500',
    '#6367A9',
    '#A05837',
    '#6B002C',
    '#772600',
    '#D790FF',
    '#9B9700',
    '#549E79',
    '#FFF69F',
    '#72418F',
    '#BC23FF',
    '#99ADC0',
    '#3A2465',
    '#922329',
    '#FDE8DC',
    '#0089A3',
    '#CB7E98',
    '#A4E804',
    '#324E72',
    '#6A3A4C',
];

const INITIAL_STATE: InteractionStudyPanelState = {
    mode: 'influencing',
    regulations: null,
    flights: null,
    step: 20,
    influencingDisplayData: null,
    influencedDisplayData: null,
    measureLoadData: null,
};

interface InteractionStudyPanelProps extends ModalPanelProps {
    measureId: MeasureId;
}

interface InteractionStudyPanelState {
    mode: 'influencing' | 'influenced';
    regulations: Array<RegulationOrMcdmOnlyDto>;
    flights: Array<FlightDto>;
    step: number;

    influencingDisplayData: DisplayData;
    influencedDisplayData: DisplayData;
    measureLoadData: MeasureLoadData;
}

export class InteractionStudyPanel extends PureComponent<InteractionStudyPanelProps, InteractionStudyPanelState> {
    private lastUpdate: Date;
    public static INITIAL_WIDTH = '80em';
    state = INITIAL_STATE;

    componentDidMount() {
        this.refresh();
    }

    componentDidUpdate(prevProps) {
        if (this.props?.measureId !== prevProps?.measureId) {
            this.setState(INITIAL_STATE, () => this.refresh());
        }
    }

    async refresh() {
        try {
            const { data: regulationData } = await this.refreshRegulations();
            if (!regulationData) throw { message: 'Failed to retrieve regulation list' };
            const { data: flightData } = await this.refreshFlightList(regulationData.measures);
            if (!flightData) throw { message: 'Failed to retrieve flight list' };
            this.computeData(regulationData.measures, flightData.flights);
        } catch (reason) {
            notification.error({ message: 'Error', description: reason.message, duration: 0 });
        }
    }

    refreshRegulations = () => {
        const request = {
            clazz: REGULATION_LIST_REQUEST_DTO_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            timePeriod: AppContext.getTodayPeriod(),
            fields: [
                RegulationFieldType.APPLICABILITY,
                RegulationFieldType.LOCATION,
                RegulationFieldType.INITIAL_CONSTRAINTS,
                RegulationFieldType.SUPPLEMENTARY_CONSTRAINTS,
                RegulationFieldType.OCCUPANCY_CONSTRAINTS,
                RegulationFieldType.REGULATION_STATE,
                RegulationFieldType.CALCULATION_TYPE,
            ],
            //regulIds: [this.props.measureId.id]
        };
        return ServiceFactory.coreService().getRegulations(request);
    };

    refreshFlightList = (regulations: Array<RegulationOrMcdmOnlyDto>) => {
        const regulation = RegulationInteractionUtils.getRegulation(regulations, this.props.measureId.id);
        if (!regulation) return Promise.reject({ message: `Failed to find regulation ${this.props.measureId.id}` });

        const req: FlightListByTvRequestDto = {
            clazz: FLIGHT_LIST_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            timeType: TimeWindowType.ENTRY,
            tvName: regulation.location.id,
            trafficWindow: regulation.applicability,
            trafficType: TrafficType.LOAD,
            fields: [
                StandardFlightFieldType.REGULATION_LOCATIONS,
                StandardFlightFieldType.MPR,
                StandardFlightFieldType.DELAY,
                StandardFlightFieldType.TIME_AT_REFLOC_ENTRY,
            ],
        };
        return ServiceFactory.coreService().getFlightList(req);
    };

    computeData = (
        regulations: Array<RegulationOrMcdmOnlyDto>,
        flights: Array<FlightDto>,
        step: number = this.state.step,
    ) => {
        const regulation = RegulationInteractionUtils.getRegulation(regulations, this.props.measureId.id);
        if (!regulation || !flights) return undefined;

        const timePeriod = TrafficCountUtils.alignedTimePeriod(regulation.applicability, step);
        const timeRange = {
            from: timePeriod.from,
            to: timePeriod.to,
            countDurationInMinutes: step,
            stepSizeInMinutes: step,
        };

        // Determine colors for regulations
        const colourPalette = [...COLOR_PALETTE].reverse();
        const colors = new Map<string, string>();
        colors.set(this.props.measureId.id, colourPalette.pop()); // Colour for studied regulation
        const otherRegs = Array.from(
            new Set(
                flights.map(flight => flight.regulationLocations.map(regLocation => regLocation.regulationId)).flat(),
            ),
        ).filter(regId => regId !== this.props.measureId.id);
        otherRegs.forEach(regId => colors.set(regId, colourPalette.pop()));

        // Remove flights that re-enter the TV, as a flight shall not be counted twice if it re-enters the regulated TV
        flights.sort((a, b) =>
            TimeUtils.diffInMs(a.timeAtReferenceLocationEntry?.time, b.timeAtReferenceLocationEntry?.time),
        );
        flights = FlightUtils.removeDuplicates(flights);

        // Calculate time slices totals, subTotals and measure load summary
        const influencingDisplayData = RegulationInteractionUtils.getInfluencingTimeSlices(timeRange, flights);
        const influenceTimeSlices = RegulationInteractionUtils.getInfluencedTimeSlices(
            this.props.measureId?.id,
            timeRange,
            flights,
        );
        const measureLoadValues = RegulationInteractionUtils.getMeasureLoadValues(
            this.props.measureId?.id,
            timeRange,
            flights,
        );
        this.lastUpdate = AppContext.getCurrentTime();
        this.setState({
            flights: flights,
            regulations: regulations,
            step: step,
            influencingDisplayData: {
                timeRange: timeRange,
                timeSlices: influencingDisplayData,
                colors: colors,
            },
            influencedDisplayData: {
                timeRange: timeRange,
                timeSlices: influenceTimeSlices,
                colors: colors,
            },
            measureLoadData: {
                timeRange: timeRange,
                loadValues: measureLoadValues,
            },
        });
    };
    render() {
        const studiedRegulation = RegulationInteractionUtils.getRegulation(
            this.state.regulations,
            this.props.measureId.id,
        );
        return (
            <Flex direction="vertical" className="interaction-study-panel">
                <Flex className="header">
                    <Flex className="date" size="15em">
                        last update{' '}
                        {TimeUtils.formatDateDDMMYYYY(this.lastUpdate, '-') +
                            ' ' +
                            TimeUtils.formatHHMM(this.lastUpdate)}
                    </Flex>
                    <Flex direction="vertical">
                        <Flex className="title">INTERACTION STUDY</Flex>
                        <Flex className="measure-id">{this.props.measureId?.id}</Flex>
                    </Flex>
                    <Flex size="15em" />
                </Flex>
                <Flex>
                    <Flex direction="vertical">
                        {/* TODO: Show the real delay repartition graph */}
                        <Row className="delay-graph" gutter={[8, 8]}>
                            <Col className="delay-graph-title" span={4}>
                                Delay repartition
                            </Col>
                            <Col className="delay-graph-load-bar" span={20}>
                                <Flex direction="vertical">
                                    <MeasureLoadBar
                                        loadValues={this.state?.measureLoadData?.loadValues}
                                        monitoredPeriod={this.state?.measureLoadData?.timeRange}
                                        barHeightEm={1}
                                        hideLabel={true}
                                    />
                                </Flex>
                            </Col>
                        </Row>
                        <Flex className="regulation-interaction" direction="vertical" size="30em">
                            <Tabs
                                activeKey={this.state.mode}
                                centered={true}
                                animated={true}
                                renderTabBar={() => (
                                    <div className="radio-buttons">
                                        <Radio.Group
                                            value={this.state.mode}
                                            onChange={value => this.setState({ mode: value.target.value })}>
                                            <Radio.Button value="influencing">Influencing</Radio.Button>
                                            <Radio.Button value="influenced">Influenced</Radio.Button>
                                        </Radio.Group>
                                    </div>
                                )}
                                items={[
                                    {
                                        label: 'influencing',
                                        key: 'influencing',
                                        children: (
                                            <RegulationInteractionTabPane
                                                data={this.state.influencingDisplayData}
                                                title={
                                                    'Repartition of the flights that are forced by other regulations'
                                                }
                                                regulation={studiedRegulation}
                                                measureId={this.props.measureId}
                                                regulations={this.state.regulations}
                                            />
                                        ),
                                    },
                                    {
                                        label: 'influenced',
                                        key: 'influenced',
                                        children: (
                                            <RegulationInteractionTabPane
                                                data={this.state.influencedDisplayData}
                                                title={
                                                    'Repartition of the flights that are forced in other regulations'
                                                }
                                                regulation={studiedRegulation}
                                                measureId={this.props.measureId}
                                                regulations={this.state.regulations}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </Flex>
                    </Flex>
                    <Flex className="settings" direction="vertical" size="10em">
                        <div className="settings-icon">
                            <SettingFilled />
                        </div>
                        <Space className="settings-text" direction="vertical">
                            Period (mn)
                            <Select
                                className="settings-selectors"
                                value={this.state.step}
                                onChange={value => this.computeData(this.state.regulations, this.state.flights, value)}>
                                <Select.Option value={20}>20</Select.Option>
                                <Select.Option value={60}>60</Select.Option>
                            </Select>
                        </Space>
                    </Flex>
                </Flex>
                <Flex className="button-panel">
                    <InteractionStudyButtonPanel measureId={this.props.measureId} onClose={this.props.onClose} />
                </Flex>
            </Flex>
        );
    }
}

interface RegulationInteractionTabPaneProps {
    data: DisplayData;
    title: string;
    measureId: MeasureId;
    regulation: RegulationOrMcdmOnlyDto;
    regulations: Array<RegulationOrMcdmOnlyDto>;
}

class RegulationInteractionTabPane extends PureComponent<RegulationInteractionTabPaneProps> {
    render() {
        return (
            <>
                <Flex className="tab-title" size="3em">
                    {this.props.title}
                </Flex>
                <Flex className="tab-body">
                    <Col span={4} />
                    <Col className="tab-graph" span={20}>
                        <RegulationInteractionGraph
                            timeRange={this.props.data?.timeRange}
                            timeSlices={this.props.data?.timeSlices}
                            colors={this.props.data?.colors}
                            regulation={this.props.regulation}
                        />
                    </Col>
                </Flex>
                <RegulationInteractionSummary
                    measureId={this.props.measureId}
                    displayData={this.props.data}
                    regulations={this.props.regulations}
                />
            </>
        );
    }
}

interface InteractionStudyButtonPanelProps extends ModalPanelProps {
    measureId: MeasureId;
}

class InteractionStudyButtonPanel extends PureComponent<InteractionStudyButtonPanelProps> {
    handleCloseClick = () => {
        this.props.onClose?.();
    };
    handleEditRegulationClick = () => {
        ModalHandler.showRegulationEditor(this.props.measureId?.id, 'reg');
    };
    render() {
        return (
            <Space>
                <Button key="close" onClick={this.handleCloseClick}>
                    Close
                </Button>
                <Button key="share" disabled={true}>
                    Share
                </Button>
                <Button key="print" disabled={true}>
                    Print
                </Button>
                <Button key="edit-regulation" onClick={this.handleEditRegulationClick}>
                    Edit regulation
                </Button>
            </Space>
        );
    }
}
